import { FC, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import { ALTERNATIVE_POSITIONS } from "../../../models/Onboarding/onboarding-values";
import CheckboxInput from "../../UI/Inputs/Checkbox";
import ErrorMessage from "../../UI/Tooltips/ErrorMessage";

// Educator/Teacher positions available for each qualification level
export const EDUCATOR_POSITIONS_BY_QUALIFICATION: Record<string, string[]> = {
  post_graduate_diploma: [
    "Certificate III Educator",
    "Assistant Educator",
    "Diploma Educator",
    "Room Leader",
    "Provisional Early Childhood Teacher",
    "Early Childhood Teacher",
    "Nominated Supervisor",
    "Assistant Centre Director",
  ],
  bachelor_or_degree: [
    "Certificate III Educator",
    "Assistant Educator",
    "Diploma Educator",
    "Room Leader",
    "Provisional Early Childhood Teacher",
    "Early Childhood Teacher",
    "Nominated Supervisor",
    "Assistant Centre Director",
  ],
  diploma: [
    "Certificate III Educator",
    "Assistant Educator",
    "Diploma Educator",
    "Room Leader",
    "Assistant Centre Director",
  ],
  certificate_iii: [
    "Certificate III Educator",
    "Assistant Educator",
    "Assistant Centre Director",
  ],
  // currently_studying sub-options
  diploma_equivalent: [
    "Certificate III Educator",
    "Assistant Educator",
    "Diploma Educator",
    "Room Leader",
    "Assistant Centre Director",
  ],
  certificate_iii_equivalent: [
    "Certificate III Educator",
    "Assistant Educator",
    "Assistant Centre Director",
  ],
};

const ALTERNATIVE_POSITIONS_LIST = [
  ALTERNATIVE_POSITIONS.ADMIN_ASSISTANT,
  ALTERNATIVE_POSITIONS.CLEANER,
  ALTERNATIVE_POSITIONS.GARDENER,
  ALTERNATIVE_POSITIONS.CHEF_COOK,
];

interface PositionTypesProps {
  /** Currently selected qualification value */
  qualification: string;
  /** Sub-option when qualification is "currently_studying" */
  currentlyStudyingOption: string;
  /** Currently selected educator/teacher positions */
  selectedEducatorPositions: string[];
  /** Currently selected alternative positions */
  selectedAlternativePositions: string[];
  /** Called when educator positions selection changes */
  onEducatorPositionsChange: (positions: string[]) => void;
  /** Called when alternative positions selection changes */
  onAlternativePositionsChange: (positions: string[]) => void;
  /** Whether all inputs are disabled */
  disabled?: boolean;
  /** Validation error for alternative positions */
  alternativePositionError?: string;
}

const PositionTypes: FC<PositionTypesProps> = ({
  qualification,
  currentlyStudyingOption,
  selectedEducatorPositions,
  selectedAlternativePositions,
  onEducatorPositionsChange,
  onAlternativePositionsChange,
  disabled,
  alternativePositionError,
}) => {
  // Derive the available educator positions from the current qualification
  const educatorPositions = useMemo(() => {
    if (qualification === "currently_studying") {
      return EDUCATOR_POSITIONS_BY_QUALIFICATION[currentlyStudyingOption] ?? [];
    }
    return EDUCATOR_POSITIONS_BY_QUALIFICATION[qualification] ?? [];
  }, [qualification, currentlyStudyingOption]);

  const allEducatorSelected =
    educatorPositions.length > 0 &&
    educatorPositions.every((pos) => selectedEducatorPositions.includes(pos));

  const allAlternativeSelected = ALTERNATIVE_POSITIONS_LIST.every((pos) =>
    selectedAlternativePositions.includes(pos)
  );

  const handleEducatorSelectAll = () => {
    if (allEducatorSelected) {
      // Remove only the positions that belong to the current educator list
      onEducatorPositionsChange(
        selectedEducatorPositions.filter(
          (pos) => !educatorPositions.includes(pos)
        )
      );
    } else {
      // Merge without duplicates
      const merged = Array.from(
        new Set([...selectedEducatorPositions, ...educatorPositions])
      );
      onEducatorPositionsChange(merged);
    }
  };

  const handleAlternativeSelectAll = () => {
    if (allAlternativeSelected) {
      onAlternativePositionsChange([]);
    } else {
      onAlternativePositionsChange([...ALTERNATIVE_POSITIONS_LIST]);
    }
  };

  const handleEducatorPositionChange = (isChecked: boolean, position: string) => {
    onEducatorPositionsChange(
      isChecked
        ? [...selectedEducatorPositions, position]
        : selectedEducatorPositions.filter((p) => p !== position)
    );
  };

  const handleAlternativePositionChange = (
    isChecked: boolean,
    position: string
  ) => {
    onAlternativePositionsChange(
      isChecked
        ? [...selectedAlternativePositions, position]
        : selectedAlternativePositions.filter((p) => p !== position)
    );
  };

  return (
    <div className="d-flex flex-column gap-20">
      <div className="fs-22 fw-400 text-black">Position Types</div>
      <div className="d-flex flex-column gap-20">
        <div className="fs-16 fw-400 text-black">
          Select options below that you would like to apply for:
        </div>

        {/* Educator / Teacher Positions — only shown when qualification unlocks them */}
        {educatorPositions.length > 0 && (
          <div className="d-flex flex-column gap-10 educator_positions_container">
            <div className="d-flex align-items-center gap-5">
              <span className="fs-16 fw-700 text-black">
                Educator/Teacher Positions:
              </span>
              <button
                type="button"
                className="btn btn-link p-0 fs-13 fw-700 text-decoration-underline text-black"
                onClick={handleEducatorSelectAll}
                disabled={disabled}
              >
                {allEducatorSelected ? "Deselect All" : "Select All"}
              </button>
            </div>
            {educatorPositions.map((position) => (
              <div key={position} className="theme-check">
                <CheckboxInput
                  checked={selectedEducatorPositions.includes(position)}
                  label={position}
                  onChange={(checked) =>
                    handleEducatorPositionChange(checked, position)
                  }
                  themeCheck={true}
                  id={uuidv4()}
                  disabled={disabled}
                />
              </div>
            ))}
          </div>
        )}

        {/* Alternative Positions — always shown */}
        <div className="d-flex flex-column gap-10 alternative_positions_container mt-10px">
          <div className="d-flex align-items-center gap-5">
            <span className="fs-16 fw-700 text-black">
              Alternative Positions:
            </span>
            <button
              type="button"
              className="btn btn-link p-0 fs-14 text-decoration-underline text-black fw-700"
              onClick={handleAlternativeSelectAll}
              disabled={disabled}
            >
              {allAlternativeSelected ? "Deselect All" : "Select All"}
            </button>
          </div>
          {ALTERNATIVE_POSITIONS_LIST.map((position) => (
            <div key={position} className="theme-check">
              <CheckboxInput
                checked={selectedAlternativePositions.includes(position)}
                label={
                  position === ALTERNATIVE_POSITIONS.CHEF_COOK
                    ? `${position}`
                    : position
                }
                onChange={(checked) =>
                  handleAlternativePositionChange(checked, position)
                }
                themeCheck={true}
                id={uuidv4()}
                disabled={disabled}
              />
            </div>
          ))}
          {alternativePositionError && (
            <ErrorMessage message={alternativePositionError} />
          )}
        </div>
      </div>
    </div>
  );
};

export default PositionTypes;
